import { Fragment, useMemo } from "react";
import { motion } from "framer-motion";
import { QuestionCard, priceCurveRolesBySurveyName } from "../analysis/QuestionCard";
import { PriceRangeCard } from "../analysis/PriceRangeCard";
import { ProgressDots } from "../analysis/QuestionNavigation";
import { getAllQuestions } from "@/lib/utils/chart-utils";
import { PopulationAnalysis } from "../profiles/PopulationAnalysis";
import type { SegmentationMapping } from "@/types/components/audiences/segmentation-mapping";
import type { CanonicalAnalysisOutput } from "@digital-twins/shared-types";
import { collapseLiftPairs } from "@/features/studies/questionnaire/assets/liftTags";
import { readTestedAssets } from "@/features/studies/report/utils/testedAssets";
import { ContentLanguageProvider } from "@/i18n/contentLanguageContext";
import {
  buildAssetMentionDisplayMap,
  normalizeQuestionAssetMentions,
  useReportQuestionCards,
} from "./useReportQuestionCards";

export {
  buildAssetMentionDisplayMap,
  normalizeQuestionAssetMentions,
  replaceAssetMentions,
} from "./useReportQuestionCards";

/**
 * Demographics gate.
 *
 * `populationAttributes` drives two independent surfaces: the Demographics
 * section below, and the `group: 'population'` options in each QuestionCard's
 * segment dropdown. Until the backend restored the segmentation drivers, both
 * were dark for report clients because the payload arrived blank
 * (`shapePopulationForClientReport`, which said so in as many words). Restoring
 * the dropdown necessarily restores this value, so the section needs its own
 * gate to stay exactly as dark as it was.
 *
 * This is the frontend half of `CLIENT_REPORT_DEMOGRAPHICS_DRIVERS_ENABLED` in
 * `apps/backend/src/studies/utils/client-survey-report-shape.ts`. Flip both
 * together, and only with the product decision behind them.
 */
const DEMOGRAPHICS_SECTION_ENABLED = false;

interface SurveyPage {
  name?: string;
  title?: string;
  elements?: any[];
  questions?: any[];
}

interface ReportDashboardProps {
  survey: any;
  responses: any[];
  populationAttributes?: string[];
  segmentationMappings?: SegmentationMapping[] | null;
  constantContext?: Record<string, string> | null;
  executiveSummary?: any;
  canonicalAnalysis?: CanonicalAnalysisOutput | null;
  studyName?: string;
  createdAt?: Date | string;
  audienceDescription?: string;
  /** When true, the internal header (title + meta) is hidden — used when rendered inside an overlay with its own header. */
  hideHeader?: boolean;
  contentLanguage?: string;
}

function extractQuestionsFromContainer(container: any): any[] {
  const questions: any[] = [];
  const elements = container.elements || container.questions || [];
  for (const el of elements) {
    if (el.type === "html" || el.type === "image") continue;
    if (el.type === "panel") {
      questions.push(...extractQuestionsFromContainer(el));
    } else if (el.name && el.type) {
      questions.push(el);
    }
  }
  return questions;
}

function isGenericPageName(value: string): boolean {
  return /^page\s*\d*$/i.test(value.trim());
}

function formatSectionTitle(page: SurveyPage, index: number): string {
  const title = page.title || "";
  const name = page.name || "";
  if (title && !isGenericPageName(title) && title !== name) return title;
  if (name && !isGenericPageName(name)) {
    return name;
  }
  return index === 0 ? "Questionnaire" : `Section ${index + 1}`;
}

function formatDate(date?: Date | string): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export function ReportDashboard({
  survey,
  responses,
  populationAttributes = [],
  segmentationMappings = null,
  constantContext = null,
  executiveSummary,
  canonicalAnalysis,
  studyName,
  createdAt,
  audienceDescription,
  hideHeader = false,
  contentLanguage,
}: ReportDashboardProps) {
  const { validResponses, segmentableQuestions } = useReportQuestionCards({
    survey,
    responses,
    segmentationMappings,
    monadicLaneScope: canonicalAnalysis?.monadicLaneScope,
  });

  const sections = useMemo(() => {
    const displayByAlias = buildAssetMentionDisplayMap(
      survey?._canonicalConfig?.assetContext?.items,
    );
    const pages: SurveyPage[] = survey?.pages || [];
    if (pages.length === 0) {
      const allQ = getAllQuestions(survey);
      return [
        {
          title: "Questionnaire",
          questions: allQ.map((question: any) =>
            normalizeQuestionAssetMentions(question, displayByAlias),
          ),
        },
      ];
    }

    return pages
      .map((page, idx) => ({
        title: formatSectionTitle(page, idx),
        questions: extractQuestionsFromContainer(page).map((question: any) =>
          normalizeQuestionAssetMentions(question, displayByAlias),
        ),
      }))
      .filter((s) => s.questions.length > 0);
  }, [survey]);

  const metaParts = [
    `${validResponses.length} respondents`,
    audienceDescription,
    formatDate(createdAt),
  ].filter(Boolean);

  const questionMap = survey?._canonicalConfig?.questionMap;
  const displayItems = useMemo(
    () =>
      collapseLiftPairs(
        sections.flatMap((section) => section.questions),
        questionMap,
      ),
    [sections, questionMap],
  );

  // Plots plan G: one "Price range" card sits above the first of the four
  // price questions (the typed role binding, never matched from wording).
  const priceQuestionNames = useMemo(
    () =>
      new Set(
        Object.values(
          priceCurveRolesBySurveyName(
            canonicalAnalysis?.reportingPlan?.priceCurve?.questionIdByRole,
            questionMap,
          ) ?? {},
        ),
      ),
    [canonicalAnalysis, questionMap],
  );
  const priceRangeAnchor = useMemo(() => {
    const first = displayItems.find((item) => {
      const itemQuestion = item.kind === "pair" ? item.pair.pre : item.question;
      return priceQuestionNames.has(itemQuestion.name);
    });
    if (!first) return null;
    return first.kind === "pair" ? first.pair.pre.name : first.question.name;
  }, [displayItems, priceQuestionNames]);

  let questionCounter = 0;

  const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: (delay: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1], delay },
    }),
  };

  return (
    <ContentLanguageProvider language={contentLanguage}>
    <div>
      {/* Header */}
      {!hideHeader && (
        <motion.header
          className="mb-24"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <h1 className="text-5xl font-semibold tracking-tight leading-[1.1] mb-5">
            {studyName || "Survey Results"}
          </h1>
          {metaParts.length > 0 && (
            <p className="text-[15px] text-muted-foreground">
              {metaParts.join(" · ")}
            </p>
          )}
        </motion.header>
      )}

      {/* Questions */}
      {displayItems.map((item) => {
        const question = item.kind === "pair" ? item.pair.pre : item.question;
        const pairedQuestion = item.kind === "pair" ? item.pair.post : undefined;
        questionCounter++;
        return (
          <Fragment key={question.name}>
          {question.name === priceRangeAnchor ? (
            <PriceRangeCard curve={canonicalAnalysis?.priceCurve} />
          ) : null}
          <motion.div
            id={question.name}
            className="scroll-mt-10 mb-[42px]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {pairedQuestion?.name && pairedQuestion.name !== question.name ? (
              <span id={pairedQuestion.name} className="sr-only" />
            ) : null}
            <QuestionCard
              question={question}
              pairedQuestion={pairedQuestion}
              questionNumber={questionCounter}
              segmentableQuestions={segmentableQuestions}
              responses={validResponses}
              populationAttributes={populationAttributes}
              canonicalAnalysis={canonicalAnalysis}
              canonicalQuestionMap={survey?._canonicalConfig?.questionMap}
              assetContextItems={survey?._canonicalConfig?.assetContext?.items}
              testedAssets={readTestedAssets(survey)}
              quoteRespondentProfiles={executiveSummary?.quoteRespondentProfiles}
              segmentationMappings={segmentationMappings}
            />
          </motion.div>
          </Fragment>
        );
      })}

      {/* Demographics */}
      {DEMOGRAPHICS_SECTION_ENABLED && populationAttributes.length > 0 && (
        <motion.div
          className="mt-6 pt-6 border-t border-border"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <h2 className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-6">
            Demographics
          </h2>
          <PopulationAnalysis
            responses={validResponses}
            analysisColumns={populationAttributes}
            segmentationMappings={segmentationMappings}
            constantContext={constantContext}
            assetContextItems={survey?._canonicalConfig?.assetContext?.items}
          />
        </motion.div>
      )}

      <div className="h-4" />

      {/* Progress dots navigation */}
      <ProgressDots
        survey={survey}
        populationAttributes={populationAttributes}
      />
    </div>
    </ContentLanguageProvider>
  );
}
